import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowRight, LogIn } from "lucide-react"
import { useAuth } from "../contexts/AuthProvider"
import Header from "../components/Header"
import Footer from "../components/Footer"
import figmaLogo from "../assets/figma.webp"
import riotLogo from "../assets/riot.webp"
import scaleLogo from "../assets/sca;e.webp"
import slbLogo from "../assets/slb.webp"
import spluckLogo from "../assets/spluck.webp"
import travleLogo from "../assets/travle.webp"
import webflowLogo from "../assets/webflow.webp"
import FreeTrialModal from "../components/FreeTrialModal"
import VideoModal from "../components/VideoModal"
import CalBookingModal from "../components/CalBookingModal"

const HomePage = () => {
  const navigate = useNavigate()
  const { user, updateUser } = useAuth()
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [currentDateTime, setCurrentDateTime] = useState("")
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)

  const timelineRef = useRef(null)
  const [scrollProgress, setScrollProgress] = useState(0)

  const [showCalModal, setShowCalModal] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return
      const rect = timelineRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight

      // How far we've scrolled through the timeline
      const totalHeight = rect.height
      const scrolled = Math.max(0, windowHeight / 2 - rect.top)
      const progress = Math.min(1, scrolled / totalHeight)

      setScrollProgress(progress)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll() // run once on mount
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Format current date and time
  useEffect(() => {
    const now = new Date()
    const options = {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      weekday: "short",
      month: "short",
      day: "numeric",
    }
    setCurrentDateTime(now.toLocaleString("en-US", options))
  }, [])

  const bookDemo = () => setShowCalModal(true)

  // Navigate to login page
  const navigateToLogin = () => {
    navigate("/login")
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header
        currentDateTime={currentDateTime}
        user={user}
        onUserChange={updateUser}
      />

      {/* ── MERGED HERO + VIDEO + LOGOS SECTION ── */}
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          display: flex;
          width: max-content;
          animation: marquee 14s linear infinite;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      <section
        className="flex flex-col bg-white"
        style={{ minHeight: "calc(100vh - 72px)" }}
      >
        {/* ── TOP: Two-column layout ── */}
        <div className="flex-1 flex items-center">
          <div className="max-w-7xl mx-auto w-full px-5 sm:px-6 lg:px-0 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* LEFT – copy */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex justify-center px-4 py-2 mb-6 rounded-full border border-gray-200 bg-gray-50">
                <span className="text-gray-500 text-xs font-medium tracking-wide">
                  The €50 problem in your hiring pipeline
                </span>
              </div>

              <h3 className="font-satoshi text-[1.6rem] leading-[1.35] xs:text-[1.9rem] sm:text-4xl lg:text-5xl font-light tracking-tight text-gray-900 mb-6">
                AI native interview platform for enterprise teams
              </h3>

              {/* Description */}
              <p className="text-base sm:text-lg text-gray-500 mb-5 font-fustat leading-relaxed max-w-sm sm:max-w-lg">
                Anyone with a €50/month tool can crack your interviews. We close
                that gap.
              </p>

              {/* Product pills */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-8">
                <span className="inline-flex items-center gap-1.5 px-0 py-1.5 rounded-full  border-gray-200 text-gray-500 text-sm">
                  <span className="text-gray-400">✦</span> Human + AI Co-pilot
                  Interviews
                </span>
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full  border-gray-200 text-gray-500 text-sm">
                  <span className="text-gray-400">✦</span> Fully AI Interviews
                </span>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto mb-4">
                <button
                  onClick={() => navigate("/demo/ai-interview")}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2 bg-white text-gray-900 rounded-lg font-semibold text-sm sm:text-base border border-gray-300 hover:border-gray-400 transition-colors"
                >
                  Try AI Interviews
                  <span className="inline-flex overflow-hidden w-0 group-hover:w-4 transition-all duration-200 ease-out">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>
                <button
                  onClick={() => navigate("/demo/ai-copilot")}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2 bg-white text-gray-900 rounded-lg font-semibold text-sm sm:text-base border border-gray-300 hover:border-gray-400 transition-colors"
                >
                  Try Human + AI Co-Pilot
                  <span className="inline-flex overflow-hidden w-0 group-hover:w-4 transition-all duration-200 ease-out">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            {/* RIGHT – embedded video card */}
            <div className="w-full">
              <div className="rounded-2xl overflow-hidden border border-gray-100">
                {/* Card header bar */}
                <div className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400" />
                  <span className="ml-2 text-gray-400 text-xs font-medium tracking-wide">
                    Product in Action
                  </span>
                </div>

                {/* 16:9 YouTube embed */}
                <div className="relative w-full h-0 pb-[56.25%] bg-black">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/fcvQ8JMwa3I?rel=0&modestbranding=1&iv_load_policy=3&disablekb=1"
                    title="Product Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BOTTOM: scrolling logos ── */}
        <div className="relative w-full bg-white overflow-hidden border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-0">
            <div className="flex items-center" style={{ height: "160px" }}>
              {/* Static "Trusted by" — hidden on mobile */}
              <div className="hidden sm:flex flex-shrink-0 items-center pr-6 z-20">
                <p className="text-gray-800 text-sm lg:text-base font-medium leading-tight whitespace-nowrap">
                  Trusted by global teams
                </p>
              </div>

              {/* Divider — hidden on mobile */}
              <div className="hidden sm:block w-px h-8 bg-gray-200 mr-6 flex-shrink-0" />

              {/* Scrolling logos */}
              <div className="relative flex-1 overflow-hidden h-full">
                {/* Fade edges */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-8 sm:w-16 z-10 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to right, #ffffff, transparent)",
                  }}
                />
                <div
                  className="absolute right-0 top-0 bottom-0 w-8 sm:w-16 z-10 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to left, #ffffff, transparent)",
                  }}
                />

                <div className="flex items-center h-full overflow-hidden">
                  <div className="marquee-track">
                    {[
                      ...[
                        { src: riotLogo, alt: "Riot Games" },
                        { src: scaleLogo, alt: "Scale" },
                        { src: slbLogo, alt: "SLB" },
                        { src: spluckLogo, alt: "Splunk" },
                        { src: travleLogo, alt: "Travelers" },
                        { src: figmaLogo, alt: "Figma" },
                        { src: webflowLogo, alt: "Webflow" },
                      ],
                      ...[
                        { src: riotLogo, alt: "Riot Games" },
                        { src: scaleLogo, alt: "Scale" },
                        { src: slbLogo, alt: "SLB" },
                        { src: spluckLogo, alt: "Splunk" },
                        { src: travleLogo, alt: "Travelers" },
                        { src: figmaLogo, alt: "Figma" },
                        { src: webflowLogo, alt: "Webflow" },
                      ],
                    ].map((logo, i) => (
                      <div
                        key={i}
                        className="flex-shrink-0 flex items-center justify-center mx-5 sm:mx-8 h-7 sm:h-9"
                      >
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="max-h-7 sm:max-h-9 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-40 bg-white font-satoshi overflow-hidden relative">
        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Heading row */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 md:mb-10 gap-4">
            <div>
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-purple-500 mb-2">
                SALIENT FEATURES
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl text-gray-900 font-[300] tracking-tight leading-tight mb-0 sm:mb-10">
                We help you hire 10x talent,{" "}
                <em className="not-italic text-gray-400">at speed</em>
              </h2>
            </div>
            <button
              onClick={bookDemo}
              className="self-start sm:self-auto inline-flex items-center gap-2 text-gray-900 text-sm font-medium border border-gray-300 px-4 py-2 rounded-lg hover:border-gray-500 hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              See it in action
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10M8 3l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200">
            {/* Card 1 — Quality */}
            <div className="bg-white p-4 sm:p-5 lg:p-6 group relative overflow-hidden flex flex-row md:flex-col gap-4">
              <div className="flex flex-col gap-3 flex-1 relative z-10">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[10px] font-medium tracking-[0.18em] uppercase text-gray-400 mb-1 block">
                      01 / Quality
                    </span>
                    <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-none tracking-tighter">
                      ATS
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1 tracking-wide">
                      Most Advanced ATS
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center group-hover:border-purple-400 group-hover:bg-purple-50 transition-colors flex-shrink-0 mt-1">
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 10L10 2M10 2H4M10 2v6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        className="text-gray-400 group-hover:text-purple-500 transition-colors"
                      />
                    </svg>
                  </div>
                </div>

                <div className="w-8 h-px bg-gray-200 group-hover:w-16 group-hover:bg-purple-400 transition-all duration-500" />

                <p className="text-gray-500 text-sm leading-relaxed">
                  Can plug into your existing job boards and filter for exact
                  requirements, rather than relying on specific keywords.
                </p>
              </div>

              {/* Image — right on mobile, bottom on desktop */}
              <div className="w-28 sm:w-36 md:w-auto md:mt-auto md:h-52 flex-shrink-0 flex items-center md:items-end relative self-center">
                <div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background:
                      "radial-gradient(ellipse at center bottom, #ede9fe 0%, transparent 70%)",
                  }}
                />
                <img
                  src="https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1771415572/home_page_gr1_prx4uk.png"
                  alt="Resume quality screening"
                  className="w-full md:h-52 object-contain rounded-lg relative z-10"
                />
              </div>
            </div>

            {/* Card 2 — Speed */}
            <div className="bg-white p-4 sm:p-5 lg:p-6 group relative overflow-hidden flex flex-row md:flex-col gap-4">
              <div className="flex flex-col gap-3 flex-1 relative z-10">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[10px] font-medium tracking-[0.18em] uppercase text-gray-400 mb-1 block">
                      02 / Speed
                    </span>
                    <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-none tracking-tighter">
                      0s
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1 tracking-wide">
                      screening delay (AI Interviews )
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center group-hover:border-amber-400 group-hover:bg-amber-50 transition-colors flex-shrink-0 mt-1">
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 10L10 2M10 2H4M10 2v6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        className="text-gray-400 group-hover:text-amber-500 transition-colors"
                      />
                    </svg>
                  </div>
                </div>

                <div className="w-8 h-px bg-gray-200 group-hover:w-16 group-hover:bg-amber-400 transition-all duration-500" />

                <p className="text-gray-500 text-sm leading-relaxed">
                  Resolut screens and interviews candidates the moment they
                  apply.
                </p>
              </div>

              <div className="w-28 sm:w-36 md:w-auto md:mt-auto md:h-52 flex-shrink-0 flex items-center md:items-end relative self-center">
                <div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background:
                      "radial-gradient(ellipse at center bottom, #fef3c7 0%, transparent 70%)",
                  }}
                />

                <img
                  src="https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1777548548/ChatGPT_Image_Apr_30_2026_04_57_08_PM_u9sr0e.png"
                  alt="Applications Dashboard"
                  className="w-full md:h-52 object-contain rounded-lg relative z-10"
                />
              </div>
            </div>

            {/* Card 3 — Cost */}
            <div className="bg-white p-4 sm:p-5 lg:p-6 group relative overflow-hidden flex flex-row md:flex-col gap-4">
              <div className="flex flex-col gap-3 flex-1 relative z-10">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[10px] font-medium tracking-[0.18em] uppercase text-gray-400 mb-1 block">
                      03 / Infra
                    </span>
                    <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-none tracking-tighter">
                      Infra
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1 tracking-wide">
                      AI native interview platform
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center group-hover:border-emerald-400 group-hover:bg-emerald-50 transition-colors flex-shrink-0 mt-1">
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 10L10 2M10 2H4M10 2v6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        className="text-gray-400 group-hover:text-emerald-500 transition-colors"
                      />
                    </svg>
                  </div>
                </div>

                <div className="w-8 h-px bg-gray-200 group-hover:w-16 group-hover:bg-emerald-400 transition-all duration-500" />

                <p className="text-gray-500 text-sm leading-relaxed">
                  Only platform that can detect AI tools like
                  FinalRound/Parakeet AI
                </p>
              </div>
              ``
              <div className="w-28 sm:w-36 md:w-auto md:mt-auto md:h-52 flex-shrink-0 flex items-center md:items-end relative self-center">
                <div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background:
                      "radial-gradient(ellipse at center bottom, #d1fae5 0%, transparent 70%)",
                  }}
                />
                <img
                  src="https://res.cloudinary.com/do0q4oj8x/image/upload/v1777620631/ip_urjap7.png"
                  alt="10000+ Applications / Day"
                  className="w-full md:h-52 object-contain rounded-lg relative z-10"
                />
              </div>
            </div>
          </div>

          <div className="mt-px bg-gray-200 h-px w-full" />
        </div>
      </section>

      {/* How It Works Section - Grid Layout */}
      <section className="py-14 md:py-16 bg-[#1a1a1a] font-satoshi overflow-hidden relative">
        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Heading row */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-purple-400 mb-2">
                How It Works
              </p>
              <h2 className="text-3xl sm:text-4xl text-white font-[300] tracking-tight leading-tight">
                Add AI to your hiring{" "}
                <em className="not-italic text-gray-500">&amp; process</em>
              </h2>
            </div>
            <button
              onClick={bookDemo}
              className="self-start sm:self-auto inline-flex items-center gap-2 text-white text-sm font-medium border border-gray-600 px-4 py-2 rounded-lg hover:border-gray-400 hover:bg-white/5 transition-colors whitespace-nowrap"
            >
              Book a Demo
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10M8 3l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Steps Grid */}
          <div className="flex flex-col gap-px bg-gray-700">
            {[
              {
                num: "01",
                label: "Screen",
                title: "Shortlist best resumes on autopilot",
                desc: "AI resume screening goes beyond keywords, identifying the most qualified candidates for the next round.",
                img: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1777565423/Screenshot_2026-04-30_at_9.40.04_PM_yo30qq.png",
                alt: "Shortlist resumes",
              },
              {
                num: "02",
                label: "Communicate",
                title: "All Your Hiring Emails, One Place",
                desc: "Built-in email app keeps all hiring communication in one place, with replies automatically forwarded to the platform.",
                img: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1773662046/Screenshot_2026-03-16_at_5.23.27_PM_knuthw.png",
                alt: "Inbuilt Email App",
                glow: "#1e3a5f",
              },
              {
                num: "03",
                label: "Interview",
                title: "Replace your round 1 interviews",
                desc: "Resolut's AI Interviews are in-depth and conversational, effectively replacing your first round of interviews.",
                img: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1773662927/Screenshot_2026-03-16_at_5.38.31_PM_xhulye.png",
                alt: "Replace round 1 interviews",
                glow: "#064020",
              },
              {
                num: "04",
                label: "Follow-up",
                title: "AI native interview platform",
                desc: "Built-in video interview platform with an in-call AI assistant, note taker, and code editor.",
                img: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1771415595/home_page_gr13_nzfi7a.png",
                alt: "AI follow-up interviews",
                glow: "#501020",
              },
              {
                num: "05",
                label: "AI Tool Detection",
                title: "Detect AI Interview Assistants in Real Time",
                desc: "It’s the only interviewing platform that can detect AI interview assistants in real time, ensuring the integrity of the interview.",
                img: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1774410304/Untitled_design_1_d41z9x.png",
                alt: "AI Cheating Tool Detection",
                glow: "#501020",
              },
              {
                num: "06",
                label: "Report",
                title: "Get detailed reports for each round",
                desc: "Rich, detailed reports ranking every candidate on technical skills, soft skills, and cultural fit.",
                img: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1777565524/Screenshot_2026-04-30_at_9.41.48_PM_f5zebg.png",
                alt: "Detailed candidate reports",
                glow: "#2e1b65",
              },
            ].map((step) => (
              <div
                key={step.num}
                className="bg-[#1a1a1a] p-6 lg:p-10 group relative overflow-hidden flex flex-col gap-6"
              >
                {/* Top row: number + title + description */}
                <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-4 md:gap-10 items-start relative z-10">
                  {/* Step number */}
                  <span className="text-5xl font-bold text-white/10 leading-none w-16 flex-shrink-0">
                    {step.num}
                  </span>

                  {/* Title + desc */}
                  <div className="py-4">
                    <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-2 block">
                      {step.label}
                    </span>
                    <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-200 text-md leading-relaxed max-w-2xl">
                      {step.desc}
                    </p>
                  </div>

                  {/* Arrow icon */}
                  <div className="hidden md:flex w-8 h-8 rounded-full border border-gray-700 items-center justify-center group-hover:border-purple-400/60 group-hover:bg-purple-500/10 transition-colors flex-shrink-0 mt-1">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 10L10 2M10 2H4M10 2v6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        className="text-gray-600 group-hover:text-purple-400 transition-colors"
                      />
                    </svg>
                  </div>
                </div>

                {/* Divider line */}
                <div className="w-10 h-px bg-gray-700 group-hover:w-20 group-hover:bg-purple-500 transition-all duration-500 relative z-10" />

                {/* Image — skipped for first card, no border radius for rest */}
                {step.img && (
                  <div className="relative w-full z-10">
                    <div className="absolute inset-0 pointer-events-none" />
                    <div className="bg-white overflow-hidden shadow-2xl">
                      <img
                        src={step.img}
                        alt={step.alt}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="h-px w-full bg-gray-700" />
        </div>
      </section>

      {/* Why Resolut Section — Improved */}
      <section
        id="features"
        className="py-10 md:py-24 bg-white font-satoshi overflow-hidden relative"
      >
        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: `linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        {/* Radial fade on grid — top center glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(237,233,254,0.45) 0%, transparent 70%)",
          }}
        />

        <style>{`
          .feature-card {
            position: relative;
            background: #fff;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            gap: 0;
            transition: box-shadow 0.3s ease;
          }
          .feature-card::before {
            content: '';
            position: absolute;
            inset: 0;
            opacity: 0;
            transition: opacity 0.4s ease;
            pointer-events: none;
          }
          .feature-card:hover::before { opacity: 1; }

          .feature-card-purple::before {
            background: radial-gradient(ellipse 80% 60% at 50% 100%, rgba(237,233,254,0.6) 0%, transparent 70%);
          }
          .feature-card-amber::before {
            background: radial-gradient(ellipse 80% 60% at 50% 100%, rgba(254,243,199,0.6) 0%, transparent 70%);
          }
          .feature-card-emerald::before {
            background: radial-gradient(ellipse 80% 60% at 50% 100%, rgba(209,250,229,0.6) 0%, transparent 70%);
          }
          .feature-card-blue::before {
            background: radial-gradient(ellipse 80% 60% at 50% 100%, rgba(219,234,254,0.6) 0%, transparent 70%);
          }

          .feature-tag-line {
            width: 2rem;
            height: 1px;
            background: #e5e7eb;
            transition: width 0.5s ease, background 0.5s ease;
          }
          .feature-card:hover .feature-tag-line-purple { width: 4rem; background: #a78bfa; }
          .feature-card:hover .feature-tag-line-amber  { width: 4rem; background: #fbbf24; }
          .feature-card:hover .feature-tag-line-emerald{ width: 4rem; background: #34d399; }
          .feature-card:hover .feature-tag-line-blue   { width: 4rem; background: #60a5fa; }

          .feature-arrow {
            transition: border-color 0.2s, background 0.2s, transform 0.2s;
          }
          .feature-card:hover .feature-arrow-purple {
            border-color: #a78bfa; background: #f5f3ff; transform: translate(1px, -1px);
          }
          .feature-card:hover .feature-arrow-amber {
            border-color: #fbbf24; background: #fffbeb; transform: translate(1px, -1px);
          }
          .feature-card:hover .feature-arrow-emerald {
            border-color: #34d399; background: #ecfdf5; transform: translate(1px, -1px);
          }
          .feature-card:hover .feature-arrow-blue {
            border-color: #60a5fa; background: #eff6ff; transform: translate(1px, -1px);
          }

          .feature-img-wrap {
            position: relative;
            overflow: hidden;
            border-top: 1px solid #f3f4f6;
            height: 350px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          @media (max-width: 640px) {
            .feature-img-wrap { height: 220px; }
            .number-stamp { font-size: 40px; }
            .badge-pill { font-size: 9px; padding: 2px 8px; }
          }
          .feature-img-wrap img {
            width: auto;
            height: 100%;
            display: block;
            transform: translateY(4px) scale(0.98);
            transition: transform 0.5s cubic-bezier(0.22,1,0.36,1);
          }
          .feature-card:hover .feature-img-wrap img {
            transform: translateY(0px) scale(1);
          }

          .badge-pill {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-size: 10px;
            font-weight: 600;
            letter-spacing: 0.15em;
            text-transform: uppercase;
            padding: 3px 10px;
            border-radius: 9999px;
            border: 1px solid;
            width: fit-content;
          }
          .badge-purple { color: #7c3aed; border-color: #ddd6fe; background: #f5f3ff; }
          .badge-amber  { color: #b45309; border-color: #fde68a; background: #fffbeb; }
          .badge-emerald{ color: #047857; border-color: #a7f3d0; background: #ecfdf5; }
          .badge-blue   { color: #1d4ed8; border-color: #bfdbfe; background: #eff6ff; }

          .number-stamp {
            font-size: clamp(56px, 8vw, 96px);
            font-weight: 900;
            line-height: 1;
            letter-spacing: -0.04em;
            color: transparent;
            -webkit-text-stroke: 1px #e5e7eb;
            user-select: none;
            pointer-events: none;
            transition: -webkit-text-stroke-color 0.3s;
          }
          .feature-card:hover .number-stamp-purple { -webkit-text-stroke-color: #ddd6fe; }
          .feature-card:hover .number-stamp-amber  { -webkit-text-stroke-color: #fde68a; }
          .feature-card:hover .number-stamp-emerald{ -webkit-text-stroke-color: #a7f3d0; }
          .feature-card:hover .number-stamp-blue   { -webkit-text-stroke-color: #bfdbfe; }
        `}</style>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* ── Section Header ── */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 sm:mb-12 gap-4 px-4 sm:px-6 lg:px-0">
            <div>
              <span className="badge-pill badge-purple mb-4">
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "#7c3aed",
                    display: "inline-block",
                  }}
                />
                True to our mission
              </span>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl text-gray-900 font-[300] tracking-tight leading-tight mt-3">
                The only tool you need
                <br />
                <em className="not-italic text-gray-400">
                  to find your next 10x hire.
                </em>
              </h2>
            </div>
            <button
              onClick={bookDemo}
              className="self-start sm:self-auto inline-flex items-center gap-2 text-gray-900 text-sm font-medium border border-gray-200 px-5 py-2.5 rounded-full hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap shadow-sm"
            >
              See it in action
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10M8 3l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* ── Feature Cards Grid ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200">
            {/* ── Card 1: AI Cheat Detection ── */}
            <div className="feature-card feature-card-purple">
              {/* Content */}
              <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-3 sm:gap-4 flex-1">
                {/* Top row */}
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <span className="badge-pill badge-purple">
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: "#7c3aed",
                          display: "inline-block",
                        }}
                      />
                      01 / Integrity
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
                      Detect AI Interview
                      <br />
                      Assistants
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="number-stamp number-stamp-purple">01</span>
                    <div className="feature-arrow feature-arrow-purple w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 10L10 2M10 2H4M10 2v6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          className="text-gray-400"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="feature-tag-line feature-tag-line-purple" />

                <p className="text-gray-500 text-sm sm:text-[15px] leading-relaxed max-w-sm">
                  Resolut is the only interviewing platform that can detect AI
                  interview assistants in real time, ensuring the integrity of
                  the interview.
                </p>
              </div>

              {/* Image */}
              <div className="feature-img-wrap">
                <img
                  src="https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1771415586/home_page_gr8_mviiun.png"
                  alt="Detect AI Interview Cheating"
                />
              </div>
            </div>

            {/* ── Card 2: Replace Round 1 ── */}
            <div className="feature-card feature-card-amber">
              <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-3 sm:gap-4 flex-1">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <span className="badge-pill badge-amber">
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: "#d97706",
                          display: "inline-block",
                        }}
                      />
                      02 / Efficiency
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
                      Replace Round 1 —<br />
                      Reduce Clutter
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="number-stamp number-stamp-amber">02</span>
                    <div className="feature-arrow feature-arrow-amber w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 10L10 2M10 2H4M10 2v6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          className="text-gray-400"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="feature-tag-line feature-tag-line-amber" />

                <p className="text-gray-500 text-sm sm:text-[15px] leading-relaxed max-w-sm">
                  State-of-the-art AI interview agents for first-round
                  interviews, probing candidates for a 360-degree evaluation
                  while saving the team's development time.
                </p>
              </div>

              <div className="feature-img-wrap">
                <img
                  src="https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1771415585/home_page_gr9_y1mk6u.png"
                  alt="Reclaim interviewing hours"
                />
              </div>
            </div>

            {/* ── Card 3: Interview Tools ── */}
            <div className="feature-card feature-card-emerald">
              <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-3 sm:gap-4 flex-1">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <span className="badge-pill badge-emerald">
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: "#059669",
                          display: "inline-block",
                        }}
                      />
                      03 / Tools
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
                      All your interview tools
                      <br />
                      in one place
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="number-stamp number-stamp-emerald">
                      03
                    </span>
                    <div className="feature-arrow feature-arrow-emerald w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 10L10 2M10 2H4M10 2v6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          className="text-gray-400"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="feature-tag-line feature-tag-line-emerald" />

                <p className="text-gray-500 text-sm sm:text-[15px] leading-relaxed max-w-sm">
                  Built-in tools like a code editor, note-taking, call
                  recording, and AI agents for 360° evaluation.
                </p>
              </div>

              <div className="feature-img-wrap">
                <img
                  src="https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1773927592/ChatGPT_Image_Mar_19_2026_07_09_20_PM_wdoyiy.png"
                  alt="AI Interviewer that feels human"
                />
              </div>
            </div>

            {/* ── Card 4: AI Agents for Recruiters ── */}
            <div className="feature-card feature-card-blue">
              <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-3 sm:gap-4 flex-1">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <span className="badge-pill badge-blue">
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: "#2563eb",
                          display: "inline-block",
                        }}
                      />
                      04 / Intelligence
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
                      AI Agents Built
                      <br />
                      for Recruiters
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="number-stamp number-stamp-blue">04</span>
                    <div className="feature-arrow feature-arrow-blue w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 10L10 2M10 2H4M10 2v6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          className="text-gray-400"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="feature-tag-line feature-tag-line-blue" />

                <p className="text-gray-500 text-sm sm:text-[15px] leading-relaxed max-w-sm">
                  Tell Resolut your requirements in plain English — no keyword
                  juggling, no boolean operators. Just describe who you need,
                  and the agent finds them.
                </p>
              </div>

              <div className="feature-img-wrap">
                <img
                  src="https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1773928363/ChatGPT_Image_Mar_19_2026_07_22_20_PM_iyjq4v.png"
                  alt="AI hiring software built for recruiters"
                />
              </div>
            </div>
          </div>

          <div className="mt-px bg-gray-200 h-px w-full" />
        </div>
      </section>

      {/* CTA Section — no image */}
      <section className="py-14 md:py-16 bg-[#1a1a1a] font-satoshi overflow-hidden relative">
        {/* Grid texture — matches How It Works */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="flex flex-col items-center text-center py-16 gap-6">
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-purple-400">
              Get Started
            </p>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl text-white font-[300] tracking-tight leading-tight max-w-2xl">
              Enough talk,{" "}
              <em className="not-italic text-gray-500">ready to try?</em>
            </h2>

            <div className="w-8 h-px bg-gray-700" />

            <p className="text-gray-300 text-md leading-relaxed max-w-sm font-fustat">
              Jump straight in — no login, no credit card, no setup. See exactly
              how Resolut transforms your hiring pipeline in minutes.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 mt-2">
              <button
                onClick={() => navigate("/demo/ai-interview")}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-gray-900 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors"
              >
                Try AI Interview
                <ArrowRight size={15} />
              </button>
              <button
                onClick={bookDemo}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-transparent text-white rounded-lg font-medium text-sm border border-gray-600 hover:border-gray-400 hover:bg-white/5 transition-colors"
              >
                Book a Demo
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 7h10M8 3l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <p className="text-gray-600 text-xs font-fustat">
              No login or credit card required
            </p>
          </div>

          <div className="h-px w-full bg-gray-700" />
        </div>
      </section>
      <Footer />

      {/* Login Required Popup - Minimalist with Blue Button */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md p-8">
            <h3 className="text-xl font-bold text-black mb-4 font-satoshi">
              Sign in required
            </h3>
            <p className="text-gray-700 mb-8 font-satoshi">
              You need to be signed in to create or join interviews. Would you
              like to sign in now?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="px-5 py-2 text-black hover:bg-gray-100 transition-colors font-satoshi"
              >
                Cancel
              </button>
              <button
                onClick={navigateToLogin}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center font-satoshi"
              >
                <LogIn size={16} className="mr-2" />
                Sign In
              </button>
            </div>
          </div>
        </div>
      )}

      <FreeTrialModal
        isOpen={showFreeTrialModal}
        onClose={() => setShowFreeTrialModal(false)}
      />

      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
      />

      <CalBookingModal
        isOpen={showCalModal}
        onClose={() => setShowCalModal(false)}
      />
    </div>
  )
}

export default HomePage
